import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Membership from "@/pages/Membership";
import Directory from "@/pages/Directory";
import Events from "@/pages/Events";
import Learning from "@/pages/Learning";
import PitchAsk from "@/pages/PitchAsk";
import Stories from "@/pages/Stories";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import MemberProfile from "@/pages/MemberProfile";
import MembersMap from "@/pages/MembersMap";
import Documents from "@/pages/Documents";
import DocumentBuilder from "@/pages/DocumentBuilder";
import AdminDashboard from "@/pages/AdminDashboard";
import Referrals from "@/pages/Referrals";
import EventGallery from "@/pages/EventGallery";
import Chapters from "@/pages/Chapters";
import ChapterDetail from "@/pages/ChapterDetail";
import PendingApproval from "@/pages/PendingApproval";
import PublicProfile from "@/pages/PublicProfile";
import Feedback from "@/pages/Feedback";
import Studio from "@/pages/Studio";
import Contracts from "@/pages/Contracts";
import Gallery from "@/pages/Gallery";
import GalleryDetail from "@/pages/GalleryDetail";
import AdminGallery from "@/pages/AdminGallery";
import AdminChapters from "@/pages/AdminChapters";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <div className="w-12 h-12 border-2 border-aubergine/20 border-t-aubergine rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Approval gate — non-approved users see PendingApproval instead of the protected page
  if (user.role !== "admin" && user.approval_status && user.approval_status !== "approved") {
    return <PendingApproval />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            style: { fontFamily: "Outfit, sans-serif", borderRadius: 12, background: "#FDFBF7", color: "#2C0E27", border: "1px solid rgba(44,14,39,0.1)" },
          }}
        />
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/directory" element={<Protected><Directory /></Protected>} />
            <Route path="/events" element={<Events />} />
            <Route path="/learning" element={<Protected><Learning /></Protected>} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/pending" element={<Protected><PendingApproval /></Protected>} />
            <Route path="/members/:id" element={<Protected><MemberProfile /></Protected>} />
            <Route path="/members-map" element={<Protected><MembersMap /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/documents/new" element={<Protected><DocumentBuilder /></Protected>} />
            <Route path="/documents/:id" element={<Protected><DocumentBuilder /></Protected>} />
            <Route path="/documents/:id/edit" element={<Protected><DocumentBuilder /></Protected>} />
            <Route path="/p/:id" element={<PublicProfile />} />
            <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
            <Route path="/pitch-ask" element={<Protected><PitchAsk /></Protected>} />
            <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
            <Route path="/create" element={<Protected><Studio /></Protected>} />
            <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/gallery/:id" element={<GalleryDetail />} />
            <Route path="/admin/gallery" element={<Protected><AdminGallery /></Protected>} />
            <Route path="/admin/chapters" element={<Protected><AdminChapters /></Protected>} />
            <Route path="/events/:id/gallery" element={<EventGallery />} />
            <Route path="/chapters" element={<Protected><Chapters /></Protected>} />
            <Route path="/chapters/:id" element={<Protected><ChapterDetail /></Protected>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </AuthProvider>
    </BrowserRouter>
  );
}
